import React, { useState, useEffect } from 'react';
import { FileText, Plus, Pencil, Trash2, Copy, Eye, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { getBranchAuthHeaders } from '@/utils/branchApi';

// Radix Select refuses an item whose value is "": "none" is this sentinel, stored as ''.
const NONE = '__none__';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const DEFAULT_TEMPLATE_CONTENT = {
  paper_width_mm: 80,
  show_header: true,
  show_order_number: true,
  show_source: true,
  show_time: true,
  show_modifiers: true,
  show_notes: true,
  show_table_number: true,
  show_server_name: false,
  bold_item_names: true,
  item_font_size: 'normal',
  header_text: '',
  footer_text: '',
  sections: [
    { type: 'header', enabled: true },
    { type: 'ticket_info', enabled: true },
    { type: 'items', enabled: true },
    { type: 'notes', enabled: true },
    { type: 'footer', enabled: true }
  ]
};

export default function KitchenTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    template_type: 'KITCHEN_TICKET',
    is_default: false,
    station_override_id: '',
    status: 'ACTIVE',
    content_json: { ...DEFAULT_TEMPLATE_CONTENT }
  });

  const fetchTemplates = async () => {
    try {
      const res = await fetch(`${API_URL}/api/kitchen/templates`, {
        headers: getBranchAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setTemplates(data);
      }
    } catch (err) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const fetchStations = async () => {
    try {
      const res = await fetch(`${API_URL}/api/kitchen/stations`, {
        headers: getBranchAuthHeaders()
      });
      if (res.ok) {
        setStations(await res.json());
      }
    } catch (err) {
      console.error('Failed to load stations');
    }
  };

  useEffect(() => {
    fetchTemplates();
    fetchStations();
  }, []);

  const openAddModal = () => {
    setEditingTemplate(null);
    setFormData({
      name: '',
      template_type: 'KITCHEN_TICKET',
      is_default: false,
      station_override_id: '',
      status: 'ACTIVE',
      content_json: { ...DEFAULT_TEMPLATE_CONTENT }
    });
    setIsModalOpen(true);
  };

  const openEditModal = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      template_type: template.template_type,
      is_default: template.is_default,
      station_override_id: template.station_override_id || '',
      status: template.status,
      content_json: template.content_json || { ...DEFAULT_TEMPLATE_CONTENT }
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const url = editingTemplate
        ? `${API_URL}/api/kitchen/templates/${editingTemplate.id}`
        : `${API_URL}/api/kitchen/templates`;

      const method = editingTemplate ? 'PATCH' : 'POST';

      const payload = {
        ...formData,
        station_override_id: formData.station_override_id || null
      };

      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...getBranchAuthHeaders()
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        toast.success(editingTemplate ? 'Template updated' : 'Template created');
        setIsModalOpen(false);
        fetchTemplates();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to save template');
      }
    } catch (err) {
      toast.error('Error saving template');
    }
  };

  const deleteTemplate = async (templateId) => {
    if (!window.confirm('Delete this template?')) return;

    try {
      const res = await fetch(`${API_URL}/api/kitchen/templates/${templateId}`, {
        method: 'DELETE',
        headers: getBranchAuthHeaders()
      });

      if (res.ok) {
        toast.success('Template deleted');
        fetchTemplates();
      } else {
        toast.error('Failed to delete template');
      }
    } catch (err) {
      toast.error('Error deleting template');
    }
  };

  const duplicateTemplate = async (template) => {
    setEditingTemplate(null);
    setFormData({
      name: `${template.name} (Copy)`,
      template_type: template.template_type,
      is_default: false,
      station_override_id: '',
      status: 'DRAFT',
      content_json: template.content_json || { ...DEFAULT_TEMPLATE_CONTENT }
    });
    setIsModalOpen(true);
  };

  const previewTemplate = async (templateId) => {
    try {
      const res = await fetch(`${API_URL}/api/kitchen/templates/${templateId}/preview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getBranchAuthHeaders()
        },
        body: JSON.stringify({})
      });

      if (res.ok) {
        const data = await res.json();
        setPreviewData(data);
        setIsPreviewOpen(true);
      } else {
        toast.error('Failed to generate preview');
      }
    } catch (err) {
      toast.error('Error generating preview');
    }
  };

  const updateContentField = (field, value) => {
    setFormData({
      ...formData,
      content_json: {
        ...formData.content_json,
        [field]: value
      }
    });
  };

  const getStationName = (stationId) => {
    const station = stations.find(s => s.id === stationId);
    return station ? station.name : stationId;
  };

  return (
    <div data-testid="kitchen-templates-page" className="p-6 dark:bg-[#011627]">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2 dark:text-[#d6deeb]">
            <FileText className="w-6 h-6" /> Ticket Templates
          </h1>
          <p className="text-muted-foreground dark:text-[#7fdbca]">
            Customize how kitchen tickets are printed
          </p>
        </div>
        <Button onClick={openAddModal} data-testid="add-template-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Template
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-10 dark:text-[#d6deeb]">Loading...</div>
      ) : templates.length === 0 ? (
        <Card className="dark:bg-[#01111d] dark:border-[#1d3b53]">
          <CardContent className="text-center py-10">
            <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground dark:text-[#5f7e97]" />
            <h3 className="text-lg font-medium dark:text-[#d6deeb]">No templates configured</h3>
            <p className="text-muted-foreground dark:text-[#7fdbca] mb-4">
              Create templates to customize your kitchen ticket prints
            </p>
            <Button onClick={openAddModal}>
              <Plus className="w-4 h-4 mr-2" /> Create Template
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {templates.map((template) => (
            <Card 
              key={template.id} 
              data-testid={`template-card-${template.id}`}
              className="dark:bg-[#01111d] dark:border-[#1d3b53]"
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg dark:text-[#d6deeb]">{template.name}</CardTitle>
                    <CardDescription className="dark:text-[#7fdbca]">
                      {template.template_type.replace('_', ' ')}
                    </CardDescription>
                  </div>
                  <div className="flex gap-1">
                    {template.is_default && (
                      <Badge variant="default" className="dark:bg-[#82aaff] dark:text-[#011627]">
                        <Check className="w-3 h-3 mr-1" />Default
                      </Badge>
                    )}
                    <Badge 
                      variant={template.status === 'ACTIVE' ? 'outline' : 'secondary'}
                      className="dark:border-[#1d3b53] dark:text-[#7fdbca]"
                    >
                      {template.status}
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-sm text-muted-foreground dark:text-[#5f7e97] mb-4 space-y-1">
                  <div>Paper: {template.content_json?.paper_width_mm || 80}mm</div>
                  {template.station_override_id && (
                    <div>Station: {getStationName(template.station_override_id)}</div>
                  )}
                  <div className="flex flex-wrap gap-1 mt-2">
                    {template.content_json?.show_modifiers && (
                      <Badge variant="outline" className="text-xs dark:border-[#1d3b53] dark:text-[#7fdbca]">
                        Modifiers
                      </Badge>
                    )}
                    {template.content_json?.show_notes && (
                      <Badge variant="outline" className="text-xs dark:border-[#1d3b53] dark:text-[#7fdbca]">
                        Notes
                      </Badge>
                    )}
                    {template.content_json?.bold_item_names && (
                      <Badge variant="outline" className="text-xs dark:border-[#1d3b53] dark:text-[#7fdbca]">
                        Bold Items
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => previewTemplate(template.id)}
                    className="dark:border-[#1d3b53] dark:text-[#d6deeb] dark:hover:bg-[#1d3b53]"
                  >
                    <Eye className="w-4 h-4 mr-1" /> Preview
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => openEditModal(template)}
                    className="dark:border-[#1d3b53] dark:text-[#d6deeb] dark:hover:bg-[#1d3b53]"
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => duplicateTemplate(template)}
                    className="dark:border-[#1d3b53] dark:text-[#d6deeb] dark:hover:bg-[#1d3b53]"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    className="text-red-500 dark:text-[#ff5874] dark:hover:bg-[#1d3b53]"
                    onClick={() => deleteTemplate(template.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Template Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto dark:bg-[#01111d] dark:border-[#1d3b53]">
          <DialogHeader>
            <DialogTitle className="dark:text-[#d6deeb]">
              {editingTemplate ? 'Edit Template' : 'Create Template'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <Tabs defaultValue="basic" className="w-full">
              <TabsList className="grid w-full grid-cols-3 dark:bg-[#011627]">
                <TabsTrigger value="basic" className="dark:data-[state=active]:bg-[#1d3b53] dark:text-[#d6deeb]">
                  Basic
                </TabsTrigger>
                <TabsTrigger value="content" className="dark:data-[state=active]:bg-[#1d3b53] dark:text-[#d6deeb]">
                  Content
                </TabsTrigger>
                <TabsTrigger value="layout" className="dark:data-[state=active]:bg-[#1d3b53] dark:text-[#d6deeb]">
                  Layout
                </TabsTrigger>
              </TabsList>

              <TabsContent value="basic" className="space-y-4 py-4">
                <div>
                  <Label className="dark:text-[#d6deeb]">Template Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Kitchen Default"
                    required
                    className="dark:bg-[#011627] dark:border-[#1d3b53] dark:text-[#d6deeb]"
                  />
                </div>

                <div>
                  <Label className="dark:text-[#d6deeb]">Template Type</Label>
                  <Select
                    value={formData.template_type}
                    onValueChange={(val) => setFormData({...formData, template_type: val})}
                  >
                    <SelectTrigger className="dark:bg-[#011627] dark:border-[#1d3b53] dark:text-[#d6deeb]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="dark:bg-[#01111d] dark:border-[#1d3b53]">
                      <SelectItem value="KITCHEN_TICKET" className="dark:text-[#d6deeb] dark:focus:bg-[#1d3b53]">
                        Kitchen Ticket
                      </SelectItem>
                      <SelectItem value="BAR_TICKET" className="dark:text-[#d6deeb] dark:focus:bg-[#1d3b53]">
                        Bar Ticket
                      </SelectItem>
                      <SelectItem value="RECEIPT" className="dark:text-[#d6deeb] dark:focus:bg-[#1d3b53]">
                        Receipt
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="dark:text-[#d6deeb]">Station Override (Optional)</Label>
                  <Select
                    value={formData.station_override_id || NONE}
                    onValueChange={(val) => setFormData({...formData, station_override_id: val === NONE ? '' : val})}
                  >
                    <SelectTrigger className="dark:bg-[#011627] dark:border-[#1d3b53] dark:text-[#d6deeb]">
                      <SelectValue placeholder="All Stations" />
                    </SelectTrigger>
                    <SelectContent className="dark:bg-[#01111d] dark:border-[#1d3b53]">
                      <SelectItem value={NONE} className="dark:text-[#d6deeb] dark:focus:bg-[#1d3b53]">
                        All Stations
                      </SelectItem>
                      {stations.map(station => (
                        <SelectItem 
                          key={station.id} 
                          value={station.id}
                          className="dark:text-[#d6deeb] dark:focus:bg-[#1d3b53]"
                        >
                          {station.name} ({station.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-sm text-muted-foreground dark:text-[#5f7e97] mt-1">
                    Apply this template only to a specific station
                  </p>
                </div>

                <div className="flex items-center justify-between">
                  <Label className="dark:text-[#d6deeb]">Set as Default</Label>
                  <Switch
                    checked={formData.is_default}
                    onCheckedChange={(checked) => setFormData({...formData, is_default: checked})}
                  />
                </div>

                <div>
                  <Label className="dark:text-[#d6deeb]">Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(val) => setFormData({...formData, status: val})}
                  >
                    <SelectTrigger className="dark:bg-[#011627] dark:border-[#1d3b53] dark:text-[#d6deeb]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="dark:bg-[#01111d] dark:border-[#1d3b53]">
                      <SelectItem value="ACTIVE" className="dark:text-[#d6deeb] dark:focus:bg-[#1d3b53]">
                        Active
                      </SelectItem>
                      <SelectItem value="DRAFT" className="dark:text-[#d6deeb] dark:focus:bg-[#1d3b53]">
                        Draft
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </TabsContent>

              <TabsContent value="content" className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center justify-between">
                    <Label className="dark:text-[#d6deeb]">Show Header</Label>
                    <Switch
                      checked={formData.content_json.show_header}
                      onCheckedChange={(checked) => updateContentField('show_header', checked)}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="dark:text-[#d6deeb]">Show Order #</Label>
                    <Switch
                      checked={formData.content_json.show_order_number}
                      onCheckedChange={(checked) => updateContentField('show_order_number', checked)}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="dark:text-[#d6deeb]">Show Source</Label>
                    <Switch
                      checked={formData.content_json.show_source}
                      onCheckedChange={(checked) => updateContentField('show_source', checked)}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="dark:text-[#d6deeb]">Show Time</Label>
                    <Switch
                      checked={formData.content_json.show_time}
                      onCheckedChange={(checked) => updateContentField('show_time', checked)}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="dark:text-[#d6deeb]">Show Modifiers</Label>
                    <Switch
                      checked={formData.content_json.show_modifiers}
                      onCheckedChange={(checked) => updateContentField('show_modifiers', checked)}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="dark:text-[#d6deeb]">Show Notes</Label>
                    <Switch
                      checked={formData.content_json.show_notes}
                      onCheckedChange={(checked) => updateContentField('show_notes', checked)}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="dark:text-[#d6deeb]">Show Table #</Label>
                    <Switch
                      checked={formData.content_json.show_table_number}
                      onCheckedChange={(checked) => updateContentField('show_table_number', checked)}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="dark:text-[#d6deeb]">Show Server</Label>
                    <Switch
                      checked={formData.content_json.show_server_name}
                      onCheckedChange={(checked) => updateContentField('show_server_name', checked)}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="dark:text-[#d6deeb]">Bold Item Names</Label>
                    <Switch
                      checked={formData.content_json.bold_item_names}
                      onCheckedChange={(checked) => updateContentField('bold_item_names', checked)}
                    />
                  </div>
                </div>

                <div>
                  <Label className="dark:text-[#d6deeb]">Header Text (Custom)</Label>
                  <Input
                    value={formData.content_json.header_text || ''}
                    onChange={(e) => updateContentField('header_text', e.target.value)}
                    placeholder="KITCHEN"
                    className="dark:bg-[#011627] dark:border-[#1d3b53] dark:text-[#d6deeb]"
                  />
                </div>

                <div>
                  <Label className="dark:text-[#d6deeb]">Footer Text (Custom)</Label>
                  <Input
                    value={formData.content_json.footer_text || ''}
                    onChange={(e) => updateContentField('footer_text', e.target.value)}
                    placeholder=""
                    className="dark:bg-[#011627] dark:border-[#1d3b53] dark:text-[#d6deeb]"
                  />
                </div>
              </TabsContent>

              <TabsContent value="layout" className="space-y-4 py-4">
                <div>
                  <Label className="dark:text-[#d6deeb]">Paper Width</Label>
                  <Select
                    value={String(formData.content_json.paper_width_mm || 80)}
                    onValueChange={(val) => updateContentField('paper_width_mm', parseInt(val))}
                  >
                    <SelectTrigger className="dark:bg-[#011627] dark:border-[#1d3b53] dark:text-[#d6deeb]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="dark:bg-[#01111d] dark:border-[#1d3b53]">
                      <SelectItem value="80" className="dark:text-[#d6deeb] dark:focus:bg-[#1d3b53]">
                        80mm (Standard)
                      </SelectItem>
                      <SelectItem value="58" className="dark:text-[#d6deeb] dark:focus:bg-[#1d3b53]">
                        58mm (Compact)
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="dark:text-[#d6deeb]">Item Font Size</Label>
                  <Select
                    value={formData.content_json.item_font_size || 'normal'}
                    onValueChange={(val) => updateContentField('item_font_size', val)}
                  >
                    <SelectTrigger className="dark:bg-[#011627] dark:border-[#1d3b53] dark:text-[#d6deeb]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="dark:bg-[#01111d] dark:border-[#1d3b53]">
                      <SelectItem value="normal" className="dark:text-[#d6deeb] dark:focus:bg-[#1d3b53]">
                        Normal
                      </SelectItem>
                      <SelectItem value="large" className="dark:text-[#d6deeb] dark:focus:bg-[#1d3b53]">
                        Large
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </TabsContent>
            </Tabs>

            <DialogFooter className="mt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setIsModalOpen(false)}
                className="dark:border-[#1d3b53] dark:text-[#d6deeb] dark:hover:bg-[#1d3b53]"
              >
                Cancel
              </Button>
              <Button type="submit">
                {editingTemplate ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Preview Modal */}
      <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
        <DialogContent className="max-w-md dark:bg-[#01111d] dark:border-[#1d3b53]">
          <DialogHeader>
            <DialogTitle className="dark:text-[#d6deeb]">Template Preview</DialogTitle>
          </DialogHeader>
          {previewData && (
            <div className="space-y-4">
              <div className="bg-white dark:bg-[#1d3b53] p-4 rounded-md border dark:border-[#5f7e97] font-mono text-xs whitespace-pre-wrap overflow-x-auto">
                {previewData.text_preview}
              </div>
              <div className="text-sm text-muted-foreground dark:text-[#7fdbca]">
                <p>Items: {previewData.item_count}</p>
                <p>Copies: {previewData.copies}</p>
                <p>Options: {JSON.stringify(previewData.resolved_options, null, 2)}</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setIsPreviewOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
